import React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import {
  Button,
  Col,
  DatePicker,
  Input,
  InputNumber,
  message,
  Radio,
  Row,
  Select,
  TimePicker,
} from 'antd';
import locale from 'antd/es/date-picker/locale/es_ES';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import moment from 'moment';
import { ICommonProps } from './common';
import CreateNewPassengerDrawer from '../../../passengers/CreateNewPassengerDrawer';
import { clientSearchFunction } from '../../../../utils/search';
import StickyFooterFormButtons from '../../../StickyFooterFormButtons/StickyFooterFormButtons';
import { Divider } from 'antd/es';
import {
  CreatePassengerMutation,
  GetAllDriversQuery,
  GetDrivingCompanyDriversDocument,
  GetDrivingCompanyDriversQuery,
  GetDrivingCompanyDriversQueryVariables,
} from '../../../../graphql/datamodel.gen';
import { getClient } from '../../../../apollo';
import AlgoliaPlaces from 'algolia-places-react';

const { TextArea } = Input;

interface IFormComponentProps extends FormComponentProps, ICommonProps {}

interface IFormComponentState {
  selectedPassengers: string[];
  allPassengers: any[];
  departureAddress?: string;
  departureLat?: number;
  departureLon?: number;
  arrivalAddress?: string;
  arrivalLat?: number;
  arrivalLon?: number;
  creatingLoading: boolean;
  createNewPassengerDrawerVisible: boolean;
  driversToSelectFrom:
    | GetAllDriversQuery['drivers']
    | GetDrivingCompanyDriversQuery['drivingCompany']['drivers'];
}

interface IFormValues {
  departureAndArrivalDates: moment.Moment[];
  departureTime: moment.Moment;
  arrivalTime: moment.Moment;
  presentationTime: moment.Moment;
  type: string;
  client?: string;
  main_driver: string;
  secondary_driver?: string;
  vehicle?: string;
  numOfPassengers: number;
  description: string;
  notes?: string;
  flightNumber?: string;
  reference?: string;
  departureAddress?: string;
  departureLat?: number;
  departureLon?: number;
  arrivalAddress?: string;
  arrivalLat?: number;
  arrivalLon?: number;
  groupName?: string;
  groupOwner?: string;
}

export const getDriversFromDrivingCompany = async (
  id: string,
): Promise<GetDrivingCompanyDriversQuery['drivingCompany']['drivers']> => {
  const drivingCompany = await getClient().query<
    GetDrivingCompanyDriversQuery,
    GetDrivingCompanyDriversQueryVariables
  >({
    query: GetDrivingCompanyDriversDocument,
    fetchPolicy: 'network-only',
    variables: {
      id,
    },
  });

  return drivingCompany.data?.drivingCompany?.drivers;
};

class CreateServiceForm extends React.Component<
  IFormComponentProps,
  IFormComponentState
> {
  state = {
    creatingLoading: false,
    createNewPassengerDrawerVisible: false,
    driversToSelectFrom: [],
    departureAddress: undefined,
    arrivalAddress: undefined,
    departureLat: undefined,
    departureLon: undefined,
    arrivalLat: undefined,
    arrivalLon: undefined,
    selectedPassengers: [],
    allPassengers: [],
  };

  constructor(props: Readonly<IFormComponentProps>) {
    super(props);
    this.state = {
      allPassengers: [...this.props.passengers],
      selectedPassengers: [],
      departureAddress: undefined,
      arrivalAddress: undefined,
      creatingLoading: false,
      createNewPassengerDrawerVisible: false,
      driversToSelectFrom: [...props.drivers],
      departureLat: undefined,
      departureLon: undefined,
      arrivalLat: undefined,
      arrivalLon: undefined,
    };
  }

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values: IFormValues) => {
      if (!err) {
        const {
          departureAndArrivalDates,
          departureTime,
          arrivalTime,
          type,
          client: clientId,
          main_driver: mainDriverId,
          secondary_driver: secondaryDriverId,
          vehicle: vehicleId,
          numOfPassengers,
          description,
          notes,
          flightNumber,
          reference,
          groupName,
          groupOwner,
          presentationTime,
        } = values;

        try {
          this.setState({
            creatingLoading: true,
          });

          const created = await this.props.createService({
            departureDate: departureAndArrivalDates[0].toISOString(),
            departureTime: moment(departureTime).toISOString(),
            arrivalDate: departureAndArrivalDates[1].toISOString(),
            arrivalTime: moment(arrivalTime).toISOString(),
            presentationTime: presentationTime.toISOString(),
            type,
            clientId,
            mainDriverId,
            secondaryDriverId,
            vehicleId,
            passengers: this.state.selectedPassengers,
            numOfPassengers,
            description,
            notes,
            flightNumber,
            reference,
            departureAddress: this.state.departureAddress,
            departureLat: this.state.departureLat,
            departureLon: this.state.departureLon,
            arrivalAddress: this.state.arrivalAddress,
            arrivalLat: this.state.arrivalLat,
            arrivalLon: this.state.arrivalLon,
            groupName,
            groupOwner,
          });

          if (created) {
            message.success({
              content: 'Servicio creado',
              key: 'create_service',
              duration: 4,
            });
            this.props.onClose();
          } else {
            message.error({
              content: 'Error al crear el servicio',
              key: 'create_service',
              duration: 4,
            });
          }
        } catch (e) {
          message.error({
            content: 'Error al crear el servicio',
            key: 'create_service',
            duration: 4,
          });
        } finally {
          this.setState({
            creatingLoading: false,
          });
          setTimeout(() => {
            message.destroy();
          }, 4500);
        }
      }
    });
  };

  onPassangerSubmit = async (
    result: boolean,
    passenger?: CreatePassengerMutation['createPassenger'],
  ) => {
    if (result && passenger?.id) {
      this.setState({
        allPassengers: [...this.state.allPassengers, passenger],
        selectedPassengers: [...this.state.selectedPassengers, passenger.id],
        createNewPassengerDrawerVisible: false,
      });
    }
  };

  render(): React.ReactNode {
    const { getFieldDecorator } = this.props.form;

    return (
      <>
        <Form layout={'vertical'} onSubmit={this.handleSubmit}>
          <Row gutter={32}>
            <Col span={12}>
              <Form.Item label="Fecha de salida y de llegada" required>
                {getFieldDecorator('departureAndArrivalDates', {
                  rules: [
                    {
                      required: true,
                      message:
                        'Por favor, introduce una fecha de salida y de llegada',
                    },
                  ],
                })(<DatePicker.RangePicker format={'DD/MM/YYYY'} />)}
              </Form.Item>
              <Form.Item
                label="Hora de salida y de llegada"
                style={{ marginBottom: 0, flexDirection: 'row' }}
                required
              >
                <Form.Item style={{ display: 'inline-block' }} required>
                  {getFieldDecorator('departureTime', {
                    rules: [
                      {
                        required: true,
                        message: 'Requerido',
                      },
                    ],
                  })(<TimePicker placeholder={'Salida'} format={'HH:mm'} />)}
                </Form.Item>
                <span
                  style={{
                    display: 'inline-block',
                    width: '24px',
                    textAlign: 'center',
                  }}
                >
                  -
                </span>
                <Form.Item
                  required
                  style={{ display: 'inline-block', width: 'calc(50% - 12px)' }}
                >
                  {getFieldDecorator('arrivalTime', {
                    rules: [
                      {
                        required: true,
                        message: 'Requerido',
                      },
                    ],
                  })(<TimePicker placeholder={'Llegada'} format={'HH:mm'} />)}
                </Form.Item>
              </Form.Item>
              <Form.Item required={true} label={'Hora de presentación'}>
                {getFieldDecorator('presentationTime', {
                  rules: [
                    {
                      required: true,
                      message: 'Requerido',
                    },
                  ],
                })(
                  <TimePicker placeholder={'Presentación'} format={'HH:mm'} />,
                )}
              </Form.Item>
              {this.props.serviceTypes.length > 0 && (
                <Form.Item label={'Tipo'} required>
                  {getFieldDecorator('type', {
                    rules: [
                      {
                        required: true,
                        message: 'Por favor, selecciona un tipo',
                      },
                    ],
                  })(
                    <Select placeholder={'Selecciona un tipo'}>
                      {this.props.serviceTypes.map((serviceType) => {
                        return (
                          <Select.Option
                            key={serviceType.id}
                            value={serviceType.id}
                          >
                            {serviceType.description}
                          </Select.Option>
                        );
                      })}
                    </Select>,

                    // <Radio.Group buttonStyle="solid">
                    //   {this.props.serviceTypes.map(serviceType => {
                    //     return (
                    //       <Radio.Button
                    //         key={serviceType.typeId}
                    //         value={serviceType.typeId}
                    //       >
                    //         {serviceType.description}
                    //       </Radio.Button>
                    //     );
                    //   })}
                    // </Radio.Group>,
                  )}
                </Form.Item>
              )}
              {this.props.isAdmin && (
                <Form.Item label={'Cliente'} required>
                  {getFieldDecorator('client', {
                    rules: [
                      {
                        required: true,
                        message: 'Por favor, selecciona un cliente',
                      },
                    ],
                  })(
                    <Select
                      showSearch
                      placeholder={'Selecciona un cliente'}
                      allowClear
                      notFoundContent={
                        'No se ha encontrado ningún cliente con ese nombre'
                      }
                      filterOption={(inputValue, option) => {
                        const clientId = option.props.value;
                        const client = this.props.clients.find(
                          (client) => client.id === clientId,
                        );

                        return clientSearchFunction(inputValue, client);
                      }}
                    >
                      {this.props.clients.map((client) => {
                        return (
                          <Select.Option key={client.id} value={client.id}>
                            {client.name}
                            <span
                              style={{
                                position: 'absolute',
                                color: '#999',
                                right: '32px',
                              }}
                            >
                              {client.nif}
                            </span>
                          </Select.Option>
                        );
                      })}
                    </Select>,
                  )}
                </Form.Item>
              )}

              <Divider />

              <Form.Item label={'Compañia de transporte colaboradora'}>
                <Select
                  filterOption={(inputValue, option) => {
                    const drivingCompanyId = option.props.value;
                    const drivingCompany = this.props.drivingCompanies.find(
                      (dc) => dc.id === drivingCompanyId,
                    );

                    return drivingCompany.name
                      ?.toLowerCase()
                      .includes(inputValue.toLowerCase());
                  }}
                  onSelect={async (value: string) => {
                    const drivers = await getDriversFromDrivingCompany(value);

                    this.setState({
                      driversToSelectFrom: drivers,
                    });
                  }}
                  onChange={(value, option) => {
                    if (!value) {
                      this.setState({
                        driversToSelectFrom: this.props.drivers,
                      });
                    }
                  }}
                  onDeselect={() => {
                    this.setState({
                      driversToSelectFrom: this.props.drivers,
                    });
                  }}
                  showSearch={true}
                  placeholder={'Filtrar por compañia colaboradora'}
                  notFoundContent={
                    'No se ha encontrado ninguna compañia con ese nombre'
                  }
                  allowClear={true}
                  // dropdownMatchSelectWidth={false}
                  dropdownStyle={{ width: '350px' }}
                >
                  {this.props.drivingCompanies.map((dCompany) => {
                    return (
                      <Select.Option key={dCompany.id} value={dCompany.id}>
                        {dCompany.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>

              <Form.Item label={'Conductores'} style={{ marginBottom: 0 }}>
                <Form.Item
                  required={false}
                  label={'Principal'}
                  style={{
                    display: 'inline-block',
                    width: 'calc(50%)',
                    paddingRight: '10px',
                  }}
                >
                  {getFieldDecorator('main_driver', {
                    rules: [
                      {
                        required: false,
                        message:
                          'Tienes que seleccionar un conductor principal',
                      },
                    ],
                  })(
                    <Select
                      showSearch
                      placeholder="Selecciona una conductor"
                      notFoundContent={
                        'No se ha encontrado ningún conductor con ese nombre'
                      }
                      allowClear
                      // dropdownMatchSelectWidth={false}
                      dropdownStyle={{ width: '350px' }}
                      filterOption={(inputValue, option) => {
                        const driverId = option.props.value;
                        const driver = this.state.driversToSelectFrom.find(
                          (driver) => driver.id === driverId,
                        );

                        return driver.name
                          ?.toLowerCase()
                          .includes(inputValue.toLowerCase());
                      }}
                    >
                      {this.state.driversToSelectFrom.map((driver) => {
                        return (
                          <Select.Option key={driver.id} value={driver.id}>
                            {driver.name}
                          </Select.Option>
                        );
                      })}
                    </Select>,
                  )}
                </Form.Item>
                <Form.Item
                  label={'Secundario'}
                  style={{
                    display: 'inline-block',
                    width: 'calc(50%)',
                    paddingLeft: '10px',
                  }}
                >
                  {getFieldDecorator('secondary_driver')(
                    <Select
                      // dropdownMatchSelectWidth={false}
                      dropdownStyle={{ width: '350px' }}
                      showSearch
                      placeholder="Selecciona una conductor"
                      allowClear
                      notFoundContent={
                        'No se ha encontrado ningún conductor con ese nombre'
                      }
                      filterOption={(inputValue, option) => {
                        const driverId = option.props.value;
                        const driver = this.state.driversToSelectFrom.find(
                          (driver) => driver.id === driverId,
                        );

                        return driver.name
                          ?.toLowerCase()
                          .includes(inputValue.toLowerCase());
                      }}
                    >
                      {this.state.driversToSelectFrom.map((driver) => {
                        return (
                          <Select.Option key={driver.id} value={driver.id}>
                            {driver.name}
                          </Select.Option>
                        );
                      })}
                    </Select>,
                  )}
                </Form.Item>
              </Form.Item>
              <Divider />
              <Form.Item label={'Vehículo'}>
                {getFieldDecorator('vehicle', {
                  rules: [
                    {
                      required: false,
                    },
                  ],
                })(
                  <Select
                    showSearch
                    notFoundContent={
                      'No se ha encontrado ningún vehículo con ese nombre'
                    }
                    placeholder="Selecciona un vehículo"
                    filterOption={(inputValue, option) => {
                      const vehicleId = option.props.value;
                      const vehicle = this.props.vehicles.find(
                        (vehicle) => vehicle.id === vehicleId,
                      );

                      return (
                        vehicle.licensePlate
                          ?.toLowerCase()
                          .includes(inputValue.toLowerCase()) ||
                        vehicle.id
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      );
                    }}
                  >
                    {this.props.vehicles.map((vehicle) => {
                      return (
                        <Select.Option key={vehicle.id} value={vehicle.id}>
                          {vehicle.licensePlate}
                        </Select.Option>
                      );
                    })}
                  </Select>,
                )}
              </Form.Item>
              <Form.Item label={'Pasajeros'}>
                <Form.Item>
                  <Select
                    value={this.state.selectedPassengers}
                    onSelect={(value) => {
                      this.setState({
                        selectedPassengers: [
                          ...this.state.selectedPassengers,
                          value,
                        ],
                      });
                    }}
                    onDeselect={(value) => {
                      this.setState({
                        selectedPassengers:
                          this.state.selectedPassengers.filter(
                            (pId) => pId !== value,
                          ),
                      });
                    }}
                    notFoundContent={
                      'No se ha encontrado ningún pasajero con ese nombre'
                    }
                    showArrow={true}
                    mode="multiple"
                    placeholder="Selecciona uno o más pasajeros"
                  >
                    {this.state.allPassengers.map((passenger) => {
                      return (
                        <Select.Option value={passenger.id}>
                          {passenger.name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
                <Form.Item label={'También puedes crear uno nuevo'}>
                  <Button
                    size={'small'}
                    onClick={() => {
                      this.setState({
                        createNewPassengerDrawerVisible: true,
                      });
                    }}
                  >
                    Crear pasajero
                  </Button>
                </Form.Item>
                <Form.Item label={'Número de pasajeros'}>
                  {getFieldDecorator('numOfPassengers', {
                    initialValue: 1,
                    rules: [
                      {
                        required: true,
                        message: 'Tienes que introducir un número de pasajeros',
                        type: 'number',
                      },
                    ],
                  })(
                    <InputNumber
                      size="large"
                      min={1}
                      max={200}
                      autoComplete={'cc-csc'}
                    />,
                  )}
                </Form.Item>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={'Descripción'} required>
                {getFieldDecorator('description', {
                  rules: [
                    {
                      required: true,
                      message: 'Tienes que escribir una descripción',
                    },
                    {
                      message: 'La descripción no puede estar en blanco',
                      whitespace: true,
                    },
                  ],
                })(
                  <TextArea
                    placeholder="Descripción del servicio"
                    autoSize={{ minRows: 3, maxRows: 5 }}
                  />,
                )}
              </Form.Item>
              <Form.Item label={'Notas'}>
                {getFieldDecorator('notes', {
                  rules: [
                    {
                      required: false,
                    },
                    {
                      whitespace: true,
                      message:
                        'La nota no puede contener únicamente carácteres en blanco',
                    },
                  ],
                })(
                  <TextArea
                    placeholder="Puedes añadir una nota"
                    autoSize={{ minRows: 2, maxRows: 3 }}
                  />,
                )}
              </Form.Item>
              <Form.Item label={'Número de vuelo'}>
                {getFieldDecorator('flightNumber', {
                  rules: [
                    {
                      required: false,
                    },
                    {
                      whitespace: true,
                      message:
                        'El número de vuelo no puede contener únicamente carácteres en blanco',
                    },
                  ],
                })(<Input size="large" placeholder="Eg: DL1994" />)}
              </Form.Item>
              <Form.Item label={'Referencia'}>
                {getFieldDecorator('reference', {
                  rules: [
                    {
                      required: false,
                    },
                    {
                      whitespace: true,
                      message:
                        'La referencia no puede contener únicamente carácteres en blanco',
                    },
                  ],
                })(<Input size="large" placeholder="Eg: X3234J" />)}
              </Form.Item>

              <Divider />
              <Form.Item label={'Lugar de salida'}>
                <AlgoliaPlaces
                  placeholder="Escribe una dirección"
                  options={{
                    appId: process.env.REACT_APP_ALGOLIA_APP_ID,
                    apiKey: process.env.REACT_APP_ALGOLIA_API_KEY,
                    countries: ['ES'],
                  }}
                  onChange={(payload) => {
                    if (payload?.suggestion?.value) {
                      this.setState({
                        departureAddress: payload?.suggestion?.value,
                        departureLat: payload?.suggestion?.latlng?.lat,
                        departureLon: payload?.suggestion?.latlng?.lng,
                      });
                    }
                  }}
                  onClear={() => {
                    this.setState({
                      departureAddress: undefined,
                      departureLat: undefined,
                      departureLon: undefined,
                    });
                  }}
                />
              </Form.Item>
              <Form.Item label={'Lugar de llegada'}>
                <AlgoliaPlaces
                  placeholder="Escribe una dirección"
                  options={{
                    appId: process.env.REACT_APP_ALGOLIA_APP_ID,
                    apiKey: process.env.REACT_APP_ALGOLIA_API_KEY,
                    countries: ['ES'],
                  }}
                  onChange={(payload) => {
                    if (payload?.suggestion?.value) {
                      this.setState({
                        arrivalAddress: payload?.suggestion?.value,
                        arrivalLat: payload?.suggestion?.latlng?.lat,
                        arrivalLon: payload?.suggestion?.latlng?.lng,
                      });
                    }
                  }}
                  onClear={() => {
                    this.setState({
                      arrivalAddress: undefined,
                      arrivalLat: undefined,
                      arrivalLon: undefined,
                    });
                  }}
                />
              </Form.Item>
              <Divider />
              {/*<Form.Item label={'Ubicación de salida'}>*/}
              {/*  <Input.Group compact>*/}
              {/*    {getFieldDecorator('departureAddress', {*/}
              {/*      rules: [*/}
              {/*        {*/}
              {/*          required: false,*/}
              {/*        },*/}
              {/*        {*/}
              {/*          whitespace: true,*/}
              {/*          message:*/}
              {/*            'La dirección no puede contener únicamente carácteres en blanco',*/}
              {/*        },*/}
              {/*      ],*/}
              {/*    })(*/}
              {/*      <Input*/}
              {/*        style={{ width: '50%' }}*/}
              {/*        placeholder={'Dirección'}*/}
              {/*      />,*/}
              {/*    )}*/}
              {/*    {getFieldDecorator('departureLocation', {*/}
              {/*      rules: [*/}
              {/*        {*/}
              {/*          required: false,*/}
              {/*        },*/}
              {/*        {*/}
              {/*          whitespace: true,*/}
              {/*          message:*/}
              {/*            'La población no puede contener únicamente carácteres en blanco',*/}
              {/*        },*/}
              {/*      ],*/}
              {/*    })(*/}
              {/*      <Input*/}
              {/*        style={{ width: '50%' }}*/}
              {/*        placeholder={'Población'}*/}
              {/*      />,*/}
              {/*    )}*/}
              {/*  </Input.Group>*/}
              {/*</Form.Item>*/}
              {/*<Form.Item label={'Ubicación de llegada'}>*/}
              {/*  <Input.Group compact>*/}
              {/*    {getFieldDecorator('arrivalAddress', {*/}
              {/*      rules: [*/}
              {/*        {*/}
              {/*          required: false,*/}
              {/*        },*/}
              {/*      ],*/}
              {/*    })(*/}
              {/*      <Input*/}
              {/*        style={{ width: '50%' }}*/}
              {/*        placeholder={'Dirección'}*/}
              {/*      />,*/}
              {/*    )}*/}
              {/*    {getFieldDecorator('arrivalLocation', {*/}
              {/*      rules: [*/}
              {/*        {*/}
              {/*          required: false,*/}
              {/*        },*/}
              {/*      ],*/}
              {/*    })(*/}
              {/*      <Input*/}
              {/*        style={{ width: '50%' }}*/}
              {/*        placeholder={'Población'}*/}
              {/*      />,*/}
              {/*    )}*/}
              {/*  </Input.Group>*/}
              {/*</Form.Item>*/}
              <Form.Item label={'Nombre del grupo'}>
                {getFieldDecorator('groupName', {
                  rules: [
                    {
                      required: false,
                    },
                    {
                      whitespace: true,
                      message:
                        'El nombre del grupo no puede contener únicamente carácteres en blanco',
                    },
                  ],
                })(<Input />)}
              </Form.Item>
              <Form.Item label={'Responsable del grupo'}>
                {getFieldDecorator('groupOwner', {
                  rules: [
                    {
                      required: false,
                    },
                    {
                      whitespace: true,
                      message:
                        'El responsable del grupo no puede contener únicamente carácteres en blanco',
                    },
                  ],
                })(<Input />)}
              </Form.Item>
            </Col>
          </Row>

          <StickyFooterFormButtons>
            <Button
              size={'large'}
              style={{ float: 'left' }}
              danger
              onClick={this.props.onClose}
            >
              Cerrar
            </Button>
            <Button
              loading={this.state.creatingLoading}
              size={'large'}
              style={{ float: 'right' }}
              type="primary"
              htmlType="submit"
            >
              Crear servicio
            </Button>
          </StickyFooterFormButtons>
        </Form>
        <CreateNewPassengerDrawer
          onClose={() => {
            this.setState({
              createNewPassengerDrawerVisible: false,
            });
          }}
          visible={this.state.createNewPassengerDrawerVisible}
          onSubmit={this.onPassangerSubmit}
        />
      </>
    );
  }
}

export const MyCreateServiceForm =
  Form.create<IFormComponentProps>()(CreateServiceForm);
